import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, File, Download, Search } from 'lucide-react';
import styles from './QBRSharedMaterials.module.css';
import bgPattern from '../../../../Assets/BRANDING-BG.png';
import { Header } from '../header/Header';
import { DashboardSidebar } from '../DashboardSidebar/DashboardSidebar';

const FOLDERS_DATA = [
    {
        id: 'csat',
        name: 'CSAT',
        fileCount: 12,
        path: '/dashboard/qbr/csat'
    },
    {
        id: 'qbr-materials',
        name: 'QBR Materials',
        fileCount: 7,
        path: '/dashboard/qbr/materials'
    },
    {
        id: 'general-documents',
        name: 'General Documents',
        fileCount: 5,
        path: '/dashboard/qbr/general-documents'
    }
];

const FILES_DATA = [
    {
        id: 1,
        name: 'Q4 2025 Customer Satisfaction Report.pdf',
        fileGroup: 'CSAT',
        size: '2.4 MB',
        status: 'Available'
    },
    {
        id: 2,
        name: 'SAP System Performance Analysis.xlsx',
        fileGroup: 'SAP Troubleshooting',
        size: '1.8 MB',
        status: 'Available'
    },
    {
        id: 3,
        name: 'Monthly Service Review - December 2025.pptx',
        fileGroup: 'General Documents',
        size: '5.2 MB',
        status: 'Available'
    },
    {
        id: 4,
        name: 'Incident Response Procedures.docx',
        fileGroup: 'General Documents',
        size: '856 KB',
        status: 'Available'
    },
    {
        id: 5,
        name: 'Q3 2025 QBR Presentation.pdf',
        fileGroup: 'CSAT',
        size: '3.1 MB',
        status: 'Available'
    },
    {
        id: 6,
        name: 'SAP Basis Configuration Guide.pdf',
        fileGroup: 'SAP Troubleshooting',
        size: '4.7 MB',
        status: 'Available'
    }
];

const FolderCard = ({ folder, onClick }) => {
    return (
        <div className={styles.folderCard} onClick={onClick}>
            <div className={styles.folderIcon}>
                <Folder size={80} />
            </div>
            <div className={styles.folderInfo}>
                <h3 className={styles.folderName}>{folder.name}</h3>
                <p className={styles.folderCount}>{folder.fileCount} files</p>
            </div>
        </div>
    );
};

export const QBRSharedMaterials = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const handleFolderClick = (folder) => {
        navigate(folder.path);
    };

    // Filter files based on search term
    const filteredFiles = FILES_DATA.filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.headerWrapper}>
                <Header onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
            </div>

            <div className={styles.mainWrapper}>
                <DashboardSidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />

                <div className={styles.container} style={{ backgroundImage: `url(${bgPattern})` }}>
                    <div className={styles.pageHeader}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.title}>Shared Materials</h1>
                            <p className={styles.subtitle}>
                                Access quarterly business review decks and shared documentation.
                            </p>
                        </div>
                        <div className={styles.headerRight}>
                            <span className={styles.lastUpdated}>
                                Last updated: <strong>Jan 15, 2026 at 09:00 AM</strong>
                            </span>
                        </div>
                    </div>

                    <div className={styles.foldersGrid}>
                        {FOLDERS_DATA.map((folder) => (
                            <FolderCard
                                key={folder.id}
                                folder={folder}
                                onClick={() => handleFolderClick(folder)}
                            />
                        ))}
                    </div>

                    <div className={styles.filesSection}>
                        <div className={styles.tableContainer}>
                            <table className={styles.filesTable}>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className={styles.fileNameHeader}>
                                                <span>File Name</span>
                                                <div className={styles.searchContainer}>
                                                    <Search size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search files..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className={styles.searchInput}
                                                    />
                                                </div>
                                            </div>
                                        </th>
                                        <th>File Group</th>
                                        <th>Size</th>
                                        <th>Status</th>
                                        <th>Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredFiles.length > 0 ? (
                                        filteredFiles.map((file) => (
                                            <tr key={file.id}>
                                                <td>
                                                    <div className={styles.fileName}>
                                                        <File size={16} />
                                                        <span>{file.name}</span>
                                                    </div>
                                                </td>
                                                <td>{file.fileGroup}</td>
                                                <td>{file.size}</td>
                                                <td>
                                                    <span className={styles.statusBadge}>
                                                        {file.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button className={styles.downloadButton}>
                                                        <Download size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className={styles.noResults}>
                                                No files found matching "{searchTerm}"
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QBRSharedMaterials;
